import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Skeleton,
  Tag,
  useColorModeValue,
  Divider,
  Select,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import type { APIAnalyticsOverview } from 'types/api.types';
import { apiGetAnalyticsOverview } from 'api';
import {
  formatCurrency,
  formatRatio,
  getRAGStatus,
  RAG_COLORS,
} from 'utils/rag';
import type { RAGStatus } from 'utils/rag';
import { TrendsChart } from './TrendsChart';
import { OverviewPerformanceTable } from './OverviewPerformanceTable';
import { PerformanceToPlanDonut } from '../../home/components/PerformanceToPlanDonut';
import Chart from 'react-apexcharts';
import type { BranchRow } from './BranchBrokerTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(v: number, decimals = 0) {
  return `${v.toFixed(decimals)}%`;
}

function fmt(v: number) {
  return formatCurrency(v);
}

function split(pl: number, cl: number, lf: number, total: number) {
  const share = (v: number) =>
    total > 0 ? ` (${formatRatio(v / total)})` : '';
  return [
    { label: 'PL', value: `${fmt(pl)}${share(pl)}` },
    { label: 'CL', value: `${fmt(cl)}${share(cl)}` },
    { label: 'L&F', value: `${fmt(lf)}${share(lf)}` },
  ];
}

function DailySalesChart({
  data,
}: {
  data: { date: string; nb_premium: number }[];
}) {
  return (
    <Chart
      type="bar"
      height={260}
      series={[
        {
          name: 'New Business',
          data: data.map((d) => Math.round(d.nb_premium)),
        },
      ]}
      options={{
        chart: { toolbar: { show: false } },
        dataLabels: { enabled: false },
        colors: ['#2C5282'],
        xaxis: { categories: data.map((d) => d.date.slice(8)) },
        yaxis: { labels: { formatter: (v: number) => fmt(v) } },
        tooltip: {
          x: {
            formatter: (
              _: number,
              { dataPointIndex }: { dataPointIndex: number },
            ) => data[dataPointIndex].date,
          },
          y: { formatter: (v: number) => fmt(v) },
        },
      }}
    />
  );
}

// ── Broker Score half-circle gauge ────────────────────────────────────────────

function BrokerScoreGauge({ score }: { score: number | null }) {
  const trackColor = useColorModeValue('#E5E7EB', '#2D3748');
  const fillPct = Math.min(1, Math.max(0, (score ?? 0) / 100));
  const r = 52;
  const cx = 68;
  const cy = 62;

  function arc(fromAngle: number, toAngle: number) {
    const sx = cx + r * Math.cos(fromAngle);
    const sy = cy + r * Math.sin(fromAngle);
    const ex = cx + r * Math.cos(toAngle);
    const ey = cy + r * Math.sin(toAngle);
    const large = toAngle - fromAngle > Math.PI ? 1 : 0;
    return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
  }

  const start = Math.PI;
  const end = 2 * Math.PI;
  const fillEnd = start + (end - start) * fillPct;

  return (
    <Flex direction="column" align="center" w="100%">
      <Text
        fontSize="10px"
        fontWeight="700"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="0.8px"
        mb="8px"
      >
        Broker Score
      </Text>
      {/* arc gauge */}
      <Box position="relative" w="160px">
        <svg
          viewBox="0 0 136 76"
          width="160"
          height="90"
          style={{ display: 'block', margin: '0 auto' }}
        >
          <path
            d={arc(start, end)}
            fill="none"
            stroke={trackColor}
            strokeWidth="14"
            strokeLinecap="round"
          />
          {fillPct > 0 && (
            <path
              d={arc(start, fillEnd)}
              fill="none"
              stroke="#2C5282"
              strokeWidth="14"
              strokeLinecap="round"
            />
          )}
          <text x="16" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF">
            0
          </text>
          <text x="120" y="72" textAnchor="middle" fontSize="9" fill="#9CA3AF">
            100
          </text>
        </svg>
      </Box>
      {/* score below arc */}
      <Text
        fontSize="40px"
        fontWeight="800"
        color="#2C5282"
        lineHeight="1"
        mt="-8px"
      >
        {score == null ? '—' : Math.round(score)}
      </Text>
      <Text fontSize="11px" color="gray.400" mt="4px">
        out of 100
      </Text>
    </Flex>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

interface TrioRow {
  label: string;
  pl: string;
  cl: string;
  lf: string;
}

interface KPICardProps {
  title: string;
  primary: string;
  sub?: string;
  trio?: { label: string; value: string }[]; // simple flat sub-items (PL/CL/L&F)
  rows?: TrioRow[]; // labelled PL/CL/L&F rows (Goal, Pace…)
  rag?: RAGStatus;
  dim?: boolean;
}

function KPICard({ title, primary, sub, trio, rows, rag, dim }: KPICardProps) {
  const bg = useColorModeValue('white', 'navy.800');
  const ragColor = rag && !dim ? RAG_COLORS[rag] : null;
  const borderCol = ragColor ? ragColor.text : '#E2E8F0';

  return (
    <Box
      bg={bg}
      borderRadius="14px"
      p="14px 16px"
      boxShadow="0px 1px 6px rgba(0,0,0,0.07)"
      borderLeft={`4px solid ${borderCol}`}
      display="flex"
      flexDirection="column"
      gap="5px"
      opacity={dim ? 0.45 : 1}
    >
      <Text
        fontSize="10px"
        fontWeight="700"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="0.8px"
      >
        {title}
      </Text>
      <Text
        fontSize="22px"
        fontWeight="800"
        color={ragColor?.text || (dim ? 'gray.400' : 'gray.800')}
        lineHeight="1.1"
      >
        {primary}
      </Text>
      {sub && (
        <Text fontSize="11px" color="gray.400">
          {sub}
        </Text>
      )}

      {/* Stacked PL / CL / L&F rows — label left, value right */}
      {trio && trio.length > 0 && (
        <Box mt="6px" display="flex" flexDirection="column" gap="5px">
          {trio.map((item) => (
            <Flex key={item.label} justify="space-between" align="baseline">
              <Text fontSize="12px" color="gray.400" fontWeight="600">
                {item.label}
              </Text>
              <Text fontSize="14px" fontWeight="700" color="gray.600">
                {item.value}
              </Text>
            </Flex>
          ))}
        </Box>
      )}

      {/* Goal / Pace rows each with PL / CL / L&F */}
      {rows && rows.length > 0 && (
        <>
          <Divider borderColor="gray.100" my="2px" />
          {rows.map((row) => (
            <Box key={row.label}>
              <Text fontSize="9px" color="gray.400" fontWeight="700" mb="2px">
                {row.label}
              </Text>
              <Flex gap="0">
                {[
                  { label: 'PL', value: row.pl },
                  { label: 'CL', value: row.cl },
                  { label: 'L&F', value: row.lf },
                ].map((item, i, arr) => (
                  <Box
                    key={item.label}
                    flex="1"
                    pr={i < arr.length - 1 ? '6px' : '0'}
                    borderRight={i < arr.length - 1 ? '1px solid' : 'none'}
                    borderColor="gray.100"
                    mr={i < arr.length - 1 ? '6px' : '0'}
                  >
                    <Text fontSize="9px" color="gray.400" fontWeight="600">
                      {item.label}
                    </Text>
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.500"
                      whiteSpace="nowrap"
                    >
                      {item.value}
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ── Broker Productivity Card ──────────────────────────────────────────────────

function BrokerProductivityCard({
  r30d,
  r12,
}: {
  r30d: number;
  r12: number | null;
}) {
  const bg = useColorModeValue('white', 'navy.800');

  return (
    <Box
      bg={bg}
      borderRadius="14px"
      p="14px 16px"
      boxShadow="0px 1px 6px rgba(0,0,0,0.07)"
      borderLeft="4px solid #E2E8F0"
      display="flex"
      flexDirection="column"
      gap="5px"
    >
      <Text
        fontSize="10px"
        fontWeight="700"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="0.8px"
      >
        Broker Productivity
      </Text>
      <Box>
        <Text fontSize="9px" color="gray.400" fontWeight="600">
          R30d Total
        </Text>
        <Text
          fontSize="20px"
          fontWeight="800"
          color="gray.800"
          lineHeight="1.1"
        >
          {r30d > 0 ? fmt(r30d) : '—'}
        </Text>
      </Box>
      <Divider borderColor="gray.100" />
      <Flex align="center" justify="space-between">
        <Text fontSize="9px" color="gray.400" fontWeight="600">
          R12 Total
        </Text>
        <Text fontSize="13px" fontWeight="700" color="gray.600">
          {r12 ? fmt(r12) : '—'}
        </Text>
      </Flex>
    </Box>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize="11px"
      fontWeight="700"
      color="gray.500"
      textTransform="uppercase"
      letterSpacing="0.8px"
      mb="14px"
    >
      {children}
    </Text>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  yearMonth: string;
  period: string;
  selectedBranchId?: number | null;
  selectedBrokerEmail?: string | null;
  brokerTable: BranchRow[];
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
}

export function AnalyticsDashboardOverview({
  yearMonth,
  period,
  selectedBranchId,
  selectedBrokerEmail,
  brokerTable,
  onBranchClick,
  onBrokerClick,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const market = searchParams.get('market');
  const businessType = searchParams.get('business_type');
  const setSalesFilter = (key: string, value: string) =>
    setSearchParams(
      (p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: true },
    );

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'analytics-overview',
      yearMonth,
      period,
      selectedBranchId,
      selectedBrokerEmail,
      market,
      businessType,
    ],
    queryFn: () =>
      apiGetAnalyticsOverview({
        year_month: yearMonth,
        period,
        ...(selectedBranchId ? { branch_id: selectedBranchId } : {}),
        ...(selectedBrokerEmail ? { broker_email: selectedBrokerEmail } : {}),
        market,
        business_type: businessType,
      }) as Promise<APIAnalyticsOverview>,
    staleTime: 10 * 60 * 1000,
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });

  const bg = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.100', 'whiteAlpha.100');
  const subColor = useColorModeValue('gray.500', 'gray.400');

  if (isLoading) {
    return (
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} height="140px" borderRadius="16px" />
        ))}
      </SimpleGrid>
    );
  }

  if (isError)
    return (
      <Text color="red.400" fontSize="sm" py="20px">
        Failed to load overview data.
      </Text>
    );
  if (!data) return null;

  const {
    sales,
    book_of_business,
    broker_activity,
    trends,
    scope,
    daily_sales,
    markets,
  } = data;
  const marketOptions =
    market && !markets.includes(market) ? [market, ...markets] : markets;

  const normTrends = (trends ?? []).map((t) => ({
    ...t,
    rag_score: getRAGStatus('score', t.score),
    rag_quotes: getRAGStatus('quotes_per_week', t.quotes),
    rag_closing: getRAGStatus('closing_ratio', t.closing_ratio),
  }));

  const ragP2P = getRAGStatus('p2p_percent', sales.p2p_percent);

  return (
    <Box>
      {/* ── Header ── */}
      <Flex align="center" mb="20px" gap="10px" wrap="wrap">
        <Text fontWeight="700" fontSize="lg" color="gray.800">
          Overview
        </Text>
        <Tag size="sm" colorScheme="brand" borderRadius="6px">
          {scope}
        </Tag>
        <Text color={subColor} fontSize="sm">
          {yearMonth}
        </Text>
        {(selectedBranchId || selectedBrokerEmail) && (
          <>
            <Divider orientation="vertical" h="16px" />
            <Text fontSize="xs" color="gray.400">
              {selectedBrokerEmail
                ? `Broker: ${selectedBrokerEmail}`
                : `Branch: ${selectedBranchId}`}
            </Text>
          </>
        )}
        <Flex gap="8px" align="center" ml={{ md: 'auto' }}>
          <Select
            size="sm"
            w="190px"
            borderRadius="8px"
            value={market ?? ''}
            onChange={(e) => setSalesFilter('market', e.target.value)}
          >
            <option value="">All markets</option>
            {marketOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
          <Select
            size="sm"
            w="170px"
            borderRadius="8px"
            value={businessType ?? ''}
            onChange={(e) => setSalesFilter('business_type', e.target.value)}
          >
            <option value="">All business types</option>
            <option value="Personal">Personal</option>
            <option value="Commercial">Commercial</option>
            <option value="Life">Life</option>
          </Select>
          {(market || businessType) && (
            <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
              Applies to New Business and Daily Sales only
            </Text>
          )}
        </Flex>
      </Flex>

      <Box
        bg={bg}
        borderRadius="16px"
        border="1px solid"
        borderColor={border}
        mb="16px"
        overflow="hidden"
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: '1fr 1px 1fr' }}
          alignItems="stretch"
          minH={{ md: '200px' }}
        >
          {/* Left: Broker Score */}
          <Flex
            direction="column"
            justify="center"
            align="center"
            px="28px"
            py="28px"
            gap="0"
          >
            <BrokerScoreGauge score={broker_activity.broker_score} />
          </Flex>

          {/* Divider 1 */}
          <Box
            bg="gray.100"
            w={{ base: '100%', md: '1px' }}
            h={{ base: '1px', md: 'auto' }}
          />

          {/* Center: P2P Donut */}
          <Flex
            direction="column"
            justify="center"
            align="center"
            px="28px"
            py="28px"
          >
            <Text
              fontSize="10px"
              fontWeight="700"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="0.8px"
              mb="12px"
              alignSelf="center"
            >
              Performance to Plan
            </Text>
            <Box w="100%" maxW="240px">
              <PerformanceToPlanDonut
                p2pPercent={sales.p2p_percent}
                paceToPlan={sales.p2p_pace}
              />
            </Box>
          </Flex>
        </Box>
      </Box>

      {/* ── Band 2: KPI cards ── */}
      <Box
        bg={bg}
        borderRadius="16px"
        border="1px solid"
        borderColor={border}
        mb="16px"
        p="20px 24px"
      >
        <SectionLabel>Key Metrics</SectionLabel>
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap="10px"
        >
          <KPICard
            title="Total Book Value"
            primary={fmt(book_of_business.tbv)}
            trio={split(
              book_of_business.tbv_pl,
              book_of_business.tbv_cl,
              book_of_business.tbv_lf,
              book_of_business.tbv,
            )}
          />

          <KPICard
            title="New Business"
            primary={fmt(sales.nb_premium_total)}
            trio={split(
              sales.nb_premium_pl,
              sales.nb_premium_cl,
              sales.nb_premium_lf,
              sales.nb_premium_total,
            )}
          />

          {/* P2P — Goal per PL/CL/L&F + Pace per PL/CL/L&F */}
          <KPICard
            title="P2P"
            primary={pct(sales.p2p_percent)}
            rag={ragP2P}
            rows={[
              {
                label: 'Goal',
                pl: fmt(sales.plan_pl),
                cl: fmt(sales.plan_cl),
                lf: fmt(sales.plan_lf),
              },
              {
                label: 'Pace',
                pl: sales.pace2p_pl > 0 ? pct(sales.pace2p_pl) : '—',
                cl: sales.pace2p_cl > 0 ? pct(sales.pace2p_cl) : '—',
                lf: sales.pace2p_lf > 0 ? pct(sales.pace2p_lf) : '—',
              },
            ]}
          />

          {/* Broker Productivity */}
          <BrokerProductivityCard
            r30d={book_of_business.broker_productivity}
            r12={book_of_business.broker_productivity_r12}
          />

          <KPICard title="Retention Rate" primary="—" dim />
          <KPICard title="Loss Ratio" primary="—" dim />
          <KPICard title="Multi-Line %" primary="—" dim />
        </Box>
      </Box>

      {/* ── Band 3: Rolling 12-Month Trend ── */}
      {normTrends.length > 0 && (
        <Box
          bg={bg}
          borderRadius="16px"
          border="1px solid"
          borderColor={border}
          mb="16px"
          p="20px 24px"
        >
          <TrendsChart data={normTrends} isAdmin trendsScope={scope} />
          <Flex gap="8px" wrap="wrap" mt="8px" align="center">
            <Text fontSize="xs" color="gray.400">
              No data yet:
            </Text>
            {[
              'Service Score',
              'Book of Business Score',
              'Loss Ratio',
              'Retention',
              'Multi-Line',
            ].map((p) => (
              <Text key={p} fontSize="xs" color="gray.300">
                {p}
              </Text>
            ))}
          </Flex>
        </Box>
      )}

      {daily_sales.length > 0 && (
        <Box
          bg={bg}
          borderRadius="16px"
          border="1px solid"
          borderColor={border}
          mb="16px"
          p="20px 24px"
        >
          <SectionLabel>Daily Sales</SectionLabel>
          <DailySalesChart data={daily_sales} />
        </Box>
      )}

      {/* ── Branch & Broker Performance ── */}
      {brokerTable.length > 0 && (
        <Box
          bg={bg}
          borderRadius="16px"
          border="1px solid"
          borderColor={border}
          mb="16px"
          p="20px 24px"
        >
          <SectionLabel>Branch & Broker Performance</SectionLabel>
          <OverviewPerformanceTable
            data={brokerTable}
            month={yearMonth}
            onBranchClick={onBranchClick}
            onBrokerClick={onBrokerClick}
          />
        </Box>
      )}
    </Box>
  );
}
